import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient, { ApiResponse, PaginationMeta } from "./apiClient";

export interface Reward {
  id: string;
  name: string;
  description: string;
  points: number;
  expiry_date: string;
  loyalty_tier_id: number;
  image_url: string;
  status: "Active" | "InActive";
  is_enabled: boolean;
  is_active: boolean;
}

export interface RewardPayload {
  name: string;
  description: string;
  points: number;
  expiry_date: string;
  loyalty_tier_id: number;
  image_url: string;
  is_enabled?: boolean;
}

export interface PaginatedRewards {
  rewards: Reward[];
  pagination: PaginationMeta;
}

const QUERY_KEY = ["rewards"];

const fetchRewards = async (page: number): Promise<PaginatedRewards> => {
  const { data: response } = await apiClient.get<ApiResponse<{ rewards: Reward[]; pagination: PaginationMeta }>>(
    "/v1/admin/rewards",
    { params: { page } }
  );
  return response.data;
};

const addReward = async (payload: RewardPayload): Promise<Reward> => {
  const { data: response } = await apiClient.post<ApiResponse<Reward>>("/v1/admin/rewards", payload);
  return response.data;
};

const updateReward = async ({ id, ...payload }: RewardPayload & { id: string }): Promise<Reward> => {
  const { data: response } = await apiClient.put<ApiResponse<Reward>>(`/v1/admin/rewards/${id}`, payload);
  return response.data;
};

const deleteReward = async (id: string): Promise<void> => {
  await apiClient.delete(`/v1/admin/rewards/${id}`);
};

export const useRewards = (page: number = 1) =>
  useQuery({
    queryKey: [...QUERY_KEY, page],
    queryFn: () => fetchRewards(page),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useAddReward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addReward,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdateReward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateReward,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteReward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteReward,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
